/**
 * Syncs MUI ThemeProvider palette mode with the shadcn/Tailwind `.dark` class
 * so leftover MUI dialogs, papers, and icons pick up dark surfaces/text.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const PJS = '"Plus Jakarta Sans", ui-sans-serif, system-ui, -apple-system, sans-serif';

function readIsDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

function buildTheme(isDark: boolean) {
  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: '#1f93ff' },
      secondary: { main: '#f50057' },
      background: {
        default: 'hsl(var(--background))',
        paper: 'hsl(var(--card))',
      },
      text: {
        primary: 'hsl(var(--foreground))',
        secondary: 'hsl(var(--muted-foreground))',
      },
      divider: 'hsl(var(--border))',
    },
    typography: {
      fontFamily: PJS,
      h1: { fontFamily: PJS, letterSpacing: '-0.02em' },
      h2: { fontFamily: PJS, letterSpacing: '-0.02em' },
      h3: { fontFamily: PJS, letterSpacing: '-0.02em' },
      h4: { fontFamily: PJS, letterSpacing: '-0.015em' },
      h5: { fontFamily: PJS, letterSpacing: '-0.01em' },
      h6: { fontFamily: PJS, letterSpacing: '-0.01em' },
      subtitle1: { fontFamily: PJS },
      subtitle2: { fontFamily: PJS },
      body1: { fontFamily: PJS },
      body2: { fontFamily: PJS },
      button: { fontFamily: PJS, textTransform: 'none', fontWeight: 500 },
      caption: { fontFamily: PJS },
      overline: { fontFamily: PJS },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: PJS,
            backgroundColor: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: 'hsl(var(--background))',
            color: 'hsl(var(--foreground))',
            border: '1px solid hsl(var(--border))',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--foreground))',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--foreground))',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: 'hsl(var(--popover))',
            color: 'hsl(var(--popover-foreground))',
            border: '1px solid hsl(var(--border))',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            // Inherit unless an explicit color prop/sx is set
            color: 'inherit',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--foreground))',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--foreground))',
            backgroundColor: 'hsl(var(--background))',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'hsl(var(--border))',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'hsl(var(--border))',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'hsl(var(--ring))',
            },
          },
          input: {
            color: 'hsl(var(--foreground))',
            '&::placeholder': {
              color: 'hsl(var(--muted-foreground))',
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: 'hsl(var(--muted-foreground))',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: 'hsl(var(--border))',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'hsl(var(--border))',
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'inherit',
            minWidth: 36,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
}

export const MuiThemeBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState(readIsDark);

  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.classList.contains('dark'));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const theme = useMemo(() => buildTheme(isDark), [isDark]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};
